/**
 * Research Sub-Masthead Section Indicator
 *
 * The right-hand element of /research/'s sticky sub-nav is a live "you are here"
 * readout: as you scroll, its label swaps to the section currently under the bar
 * (Predictions → Whitepapers → Practitioner Accounts → …) and its accent recolours
 * to that section's tag colour, with a fade. Before this, nothing tracked scroll —
 * the amber "Predictions" tag sat static and read as a stuck active state.
 *
 * The server renders the indicator with a data-section-map (id → {label, Tailwind
 * colour class}). This island resolves each id to its section element, orders them
 * by document position, and on scroll picks the last section whose top has crossed
 * the trigger line (fixed 72px site nav + ~54px sticky bar); above the first section
 * the first entry stays shown. The accent is a Tailwind class swapped on the anchor
 * (the dot inherits it via bg-current) — no hex, no inline styles — and the label
 * crossfades via an opacity utility. transition-colors handles the colour fade.
 *
 * Progressive enhancement (house style — see research-index-filter-island.ts): with
 * JS off, the indicator stays a plain amber "Predictions" jump-link and every anchor
 * still jumps to its section. Behaviour-only island: self-boots on load like
 * cite-modal-island, the house pattern for directly-scripted islands.
 */

// A section counts as "current" once it reaches its anchor-SETTLE line — where a
// click parks it: `html { scroll-padding-top: 72px }` (the fixed nav) STACKS with the
// tracked sections' own `scroll-mt-24` (96px scroll-margin-top) ≈ 168px from the top.
// The trigger must sit at/below that settle point; otherwise a clicked section lands
// BELOW the line, never counts as reached, and the previous section stays highlighted
// (the "snaps back to Predictions when the scroll stops" bug). +8px absorbs rounding;
// the lanes are far taller than this, so the next section never wins prematurely.
const TRIGGER_PX = 176; // scroll-padding-top (72) + section scroll-mt-24 (96) + 8 rounding
const FADE_MS = 150; // matches the label's `transition duration-150`

interface SectionEntry {
  id: string;
  label: string;
  colorClass: string;
}
interface TrackedSection extends SectionEntry {
  el: HTMLElement;
}

function boot(): void {
  const indicator = document.querySelector<HTMLAnchorElement>('[data-section-indicator]');
  const labelEl = indicator?.querySelector<HTMLElement>('[data-indicator-label]');
  // The dot colours itself via `bg-current` off the anchor's text colour, so the
  // island never touches it — only the label + accent class + aria-label move.
  if (!indicator || !labelEl) return;

  let entries: SectionEntry[];
  try {
    entries = JSON.parse(indicator.getAttribute('data-section-map') ?? '[]') as SectionEntry[];
  } catch {
    return;
  }

  const sections: TrackedSection[] = entries
    .map((e): TrackedSection | null => {
      // Validate the (server-controlled, but unchecked-cast) shape at the boundary,
      // so a malformed entry drops out here rather than throwing at classList.add.
      if (!e || typeof e.id !== 'string' || typeof e.label !== 'string' || typeof e.colorClass !== 'string') {
        return null;
      }
      const el = document.getElementById(e.id);
      return el ? { ...e, el } : null;
    })
    .filter((s): s is TrackedSection => s !== null)
    .sort((a, b) =>
      a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    );

  const first = sections[0];
  if (!first) return;

  let currentId = first.id;
  let currentColorClass = first.colorClass;
  let fadeTimer = 0;

  function applySection(s: TrackedSection): void {
    if (s.id === currentId) return;
    currentId = s.id;
    if (s.colorClass !== currentColorClass) {
      indicator!.classList.remove(currentColorClass);
      indicator!.classList.add(s.colorClass);
      currentColorClass = s.colorClass;
    }
    indicator!.setAttribute('href', `#${s.id}`);
    indicator!.setAttribute('aria-label', `Current section: ${s.label}`);
    // Crossfade the label text swap; colour morphs underneath via transition-colors.
    labelEl!.classList.add('opacity-0');
    if (fadeTimer) window.clearTimeout(fadeTimer);
    fadeTimer = window.setTimeout(() => {
      if (currentId === s.id) {
        labelEl!.textContent = s.label;
        labelEl!.classList.remove('opacity-0');
      }
    }, FADE_MS);
  }

  // While a click-initiated smooth scroll is in flight, hold the clicked section
  // instead of flashing through every section the scroll passes over on the way.
  let lockedId: string | null = null;
  let lockTimer = 0;

  function update(): void {
    let winner: TrackedSection = first!;
    for (const s of sections) {
      if (s.el.getBoundingClientRect().top <= TRIGGER_PX) winner = s;
    }
    if (lockedId !== null) {
      // Release only once the scroll has actually arrived at the clicked section
      // (the natural winner catches up); until then suppress the transient so the
      // indicator doesn't show the previous section mid-scroll.
      if (winner.id === lockedId) {
        lockedId = null;
        if (lockTimer) {
          window.clearTimeout(lockTimer);
          lockTimer = 0;
        }
      } else {
        return;
      }
    }
    applySection(winner);
  }

  // Clicking any in-page link to a tracked section reflects it immediately, so the
  // right-hand indicator never lags behind the smooth scroll (the previous-section
  // flash Karen reported).
  document.addEventListener('click', (event: MouseEvent) => {
    const link = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href^="#"]');
    if (!link) return;
    const raw = link.getAttribute('href') ?? '';
    let id: string;
    try {
      id = decodeURIComponent(raw.slice(1));
    } catch {
      id = raw.slice(1);
    }
    const target = sections.find((s) => s.id === id);
    if (!target) return;
    lockedId = target.id;
    applySection(target);
    // Safety net: never hold the lock longer than a scroll could plausibly take,
    // in case the target never becomes the natural winner (e.g. a short last section).
    if (lockTimer) window.clearTimeout(lockTimer);
    lockTimer = window.setTimeout(() => {
      lockedId = null;
      lockTimer = 0;
    }, 1200);
  });

  let ticking = false;
  const onScrollOrResize = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      update();
    });
  };
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  update();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

export {};
